import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import { CartContext } from "../context/CartContext";
import RelatedProducts from "../components/RelatedProducts";
import ProductReviews from "../components/ProductReviews";
import { fetchFamilyVariants, getFamilyDisplayInfo, getVariantOptionLabel } from "../utils/productFamily";
import "./ProductPage.css";

function ProductPage() {

  const { slug } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const [currentTitle, setCurrentTitle] = useState("");
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [familyProducts, setFamilyProducts] = useState([]);
  const [lensStyle, setLensStyle] = useState({});
  const [showLens, setShowLens] = useState(false);

  const { addToCart, clearCart, setShowCart } = useContext(CartContext);

  const getSelectedCartItem = () => ({
    ...product,
    price: salePrice,
    image: mainImage,
    cartKey: product.id,
    images: [
      {
        ...(product.images?.[0] || {}),
        src: mainImage,
        alt: product.name
      }
    ]
  });

  // -----------------------------------
  // FETCH PRODUCT
  // -----------------------------------

  useEffect(() => {

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);

    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        const cleanSlug = decodeURIComponent(slug).toLowerCase().trim();
        const response = await fetch(
          `https://data.lacegota.in/wp-json/wc/store/v1/products?slug=${cleanSlug}`
        );

        if (!response.ok) {
          throw new Error("Server down");
        }

        const data = await response.json();
        const foundItem = data?.[0];

        if (foundItem) {
          const matchingFamily = await fetchFamilyVariants(foundItem);
          setFamilyProducts(matchingFamily);
          setProduct(foundItem);
          setCurrentTitle(foundItem.name);
          setMainImage(
            foundItem.images?.[0]?.src || "https://via.placeholder.com/500"
          );
        } else {
          setProduct(null);
        }
      } catch (err) {
        console.error("Product Fetch Error:", err);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();

    return () => {
      window.removeEventListener("resize", handleResize);
    };

  }, [slug]);

  if (loading) {
    return <div className="loader">Loading Product...</div>;
  }

  if (!product) {
    return <div className="loader">Product not found</div>;
  }

  // -----------------------------------
  // PRICE LOGIC
  // -----------------------------------

  const salePrice =
    product.prices?.price
      ? Number(product.prices.price) / 100
      : 0;

  const regPrice =
    product.prices?.regular_price
      ? Number(product.prices.regular_price) / 100
      : salePrice;

  const discountPerc =
    regPrice > salePrice
      ? Math.round(((regPrice - salePrice) / regPrice) * 100)
      : 0;

  // -----------------------------------
  // ATTRIBUTES
  // -----------------------------------

  const sizeAttr =
    product.attributes?.find(
      a => a.name?.toLowerCase() === "size"
    );

  const productSize =
    sizeAttr?.options?.[0]
    || sizeAttr?.terms?.[0]?.name
    || "";

  const valueAttr =
    product.attributes?.find(
      a => a.name?.toLowerCase() === "values"
    );

  const productValue =
    valueAttr?.options?.[0]
    || valueAttr?.terms?.[0]?.name
    || "";

  const familyDisplay = getFamilyDisplayInfo(product, familyProducts);
  const availableVariantCount = familyDisplay.displayCount;
  const availableVariantType = familyDisplay.displayLabel;
  const variantGroupLabel = familyDisplay.isCentralFamily && !familyDisplay.usesVerifiedColorNames
    ? "Options"
    : "Colour";

  // -----------------------------------
  // BUY NOW
  // -----------------------------------

 const handleAddToCart = () => {
  addToCart(getSelectedCartItem(), qty);
 };

  const handleBuyNow = () => {

    clearCart();
    addToCart(getSelectedCartItem(), qty);
    setShowCart(false);
  
    navigate("/checkout");

  };

 const handleVariantClick = (selectedProduct) => {
  setProduct(selectedProduct);
  setCurrentTitle(selectedProduct.name);
  setMainImage(
    selectedProduct.images?.[0]?.src || "https://via.placeholder.com/500"
  );
  navigate(`/product/${selectedProduct.slug}`, { replace: true });
 };
  // -----------------------------------
  // IMAGE ZOOM
  // -----------------------------------

  const handleMouseMove = (e) => {

    if (isMobile) return;

    const rect =
      e.currentTarget.getBoundingClientRect();

    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;

    const lensSize = 150;

    if (x < lensSize / 2) x = lensSize / 2;
    if (y < lensSize / 2) y = lensSize / 2;

    if (x > rect.width - lensSize / 2) {
      x = rect.width - lensSize / 2;
    }

    if (y > rect.height - lensSize / 2) {
      y = rect.height - lensSize / 2;
    }

    setLensStyle({
      left: x - lensSize / 2 + "px",
      top: y - lensSize / 2 + "px",
      backgroundPosition:
        `${(x / rect.width) * 100}% ${(y / rect.height) * 100}%`
    });

    setShowLens(true);

  };

  return (

    <div className="productPageWrapper">

      {/* BREADCRUMB */}

      <div className="breadcrumb">
        <Link to="/">Home</Link> / {currentTitle}
      </div>

      <div className={isMobile ? "mobileLayout" : "desktopLayout"}>

        {/* IMAGE SECTION */}

        <div className="imgContainer">
          {/* LEFT SIDE SINGLE THUMBNAIL */}

            {product.images?.[0] && (

              <div
                style={{
                  marginRight: "12px"
                }}
              >

                <img
                  src={product.images[0].src}
                  alt="thumb"

                  onClick={() => {
                    setMainImage(product.images[0].src);
                    setCurrentTitle(product.name);
                  }}

                  style={{
                    width: "58px",
                    height: "58px",
                    objectFit: "cover",
                    border:
                      mainImage === product.images[0].src
                        ? "2px solid #ff9900"
                        : "1px solid #ddd",
                    borderRadius: "6px",
                    cursor: "pointer",
                    padding: "2px",
                    background: "#fff"
                  }}
                />

              </div>

            )}

          <div
            className="mainImgWrap"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setShowLens(false)}
          >

            <img
              src={mainImage}
              className="fullImg"
              alt={currentTitle}
            />

            {showLens && (
              <div
                className="lens"
                style={lensStyle}
              ></div>
            )}

            {showLens && (
              <div
                className="zoomRes"
                style={{
                  backgroundImage: `url(${mainImage})`,
                  backgroundPosition: lensStyle.backgroundPosition
                }}
              ></div>
            )}

          </div>

        </div>

        {/* PRODUCT DETAILS */}

        <div className="pDetails">

          <h1 className="amazonTitle">
            {currentTitle}
          </h1>

          <p className="pRating">
            ⭐ 4.2
            <span className="ratingCount">
              (10 reviews)
            </span>
          </p>

          <hr />

          {/* PRICE */}

          <div className="priceSection">

            <div className="mainPriceRow">

              {discountPerc > 0 && (
                <span className="pPercentage">
                  -{discountPerc}%
                </span>
              )}

              <span className="pPriceBold">
                ₹{salePrice}
              </span>

            </div>

            {regPrice > salePrice && (
              <div className="mktPriceRow">
                M.R.P.:
                <span className="pStrike">
                  ₹{regPrice}
                </span>
              </div>
            )}

            {/* UNIT */}

            {(productSize || productValue) && (

              <div
                style={{
                  marginTop: "10px",
                  padding: "5px 12px",
                  background: "#fff7ed",
                  border: "1px solid #fb923c",
                  borderRadius: "5px",
                  display: "inline-block",
                  color: "#c2410c",
                  fontWeight: "700",
                  fontSize: "0.95rem"
                }}
              >

                📏 Unit:

                {productSize && ` ${productSize}`}

                {productValue && ` • ${productValue}`}

              </div>

            )}

            {availableVariantCount > 0 && (
              <p className="availableInText">
                Available in {availableVariantCount} {availableVariantType}
              </p>
            )}

            {/* COLOUR OPTIONS UNDER UNIT */}

            {familyProducts.length > 1 && (

              <div className="variantSection">

                <p className="variantLabel">
                  {variantGroupLabel}: <strong>{getVariantOptionLabel(product)}</strong>
                </p>

                <div className="variantGrid">

                  {familyProducts.map((variant) => (

                    <button
                      type="button"
                      key={variant.id}
                      className={`variantOption ${variant.id === product.id ? "active" : ""}`}
                      onClick={() => handleVariantClick(variant)}
                      title={getVariantOptionLabel(variant)}
                      aria-label={`Select ${getVariantOptionLabel(variant)} option`}
                    >

                      <img
                        src={variant.images?.[0]?.src || "https://via.placeholder.com/100"}
                        alt={getVariantOptionLabel(variant)}
                        className="variantThumb"
                      />
                      <span>{getVariantOptionLabel(variant)}</span>
                    </button>

                  ))}
                </div>

              </div>

            )}

          </div>

          {/* DESCRIPTION */}

          <div
            className="pDesc"
            style={{ marginTop: "25px" }}
            dangerouslySetInnerHTML={{
              __html: product.description
            }}
          />

        </div>

        {/* BUY BOX */}

        {!isMobile && (

          <div className="pBuyBox">

            <div className="buyAmount">
              ₹{salePrice}
            </div>

            <p className={`stockStatus ${product.is_in_stock === false ? "outOfStock" : ""}`}>
              {product.is_in_stock === false ? "Out of Stock" : "In Stock"}
            </p>

            {productSize && (
              <p
                style={{
                  fontSize: "0.9rem",
                  color: "#666"
                }}
              >
                Sold in:
                <b> {productSize}</b>
              </p>
            )}

            <div
              className="qty-row"
              style={{
                marginBottom: "15px",
                marginTop: "10px"
              }}
            >

              <label>Packs: </label>

              <select
                value={qty}
                onChange={(e) =>
                  setQty(Number(e.target.value))
                }
              >

                {[1, 2, 3, 4, 5].map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}

              </select>

            </div>

            <button
              className="addBtn"
              onClick={handleAddToCart}
            >
              Add to Cart
            </button>

            <button
              className="buyBtn"
              onClick={handleBuyNow}
            >
              Buy Now
            </button>

          </div>

        )}

      </div>

      {/* MOBILE ACTIONS */}

      {isMobile && (

        <div className="mActions">

          <button
            className="mAddBtn"
            onClick={handleAddToCart}
          >
            Add to Cart
          </button>

          <button
            className="mBuyBtn"
            onClick={handleBuyNow}
          >
            Buy Now
          </button>

        </div>

      )}

      {/* EXTRA */}

      <div className="extra-sections">

        {product.categories?.[0]?.id && (
          <RelatedProducts
            categoryId={product.categories[0].id}
          />
        )}

        <ProductReviews
          productId={product.id}
        />

      </div>

    </div>

  );

}

export default ProductPage;


